<template>
  <page-layout
      :title="pageTitle"
      :breadcrumb="pageBreadcrumb"
      :info-cards="pageInfoCards"
  >
    <template #actions>
      <md-button class="md-primary md-round" @click="goToAdd">
        <md-icon>add</md-icon>
        Add Role
      </md-button>
      <md-button class="md-info md-round" @click="refreshList" :disabled="isLoading">
        <md-icon>refresh</md-icon>
        Refresh
      </md-button>
    </template>

    <template #content>
      <!-- Filters -->
      <div class="filters-bar">
        <div class="md-layout md-gutter">
          <div class="md-layout-item md-size-30 md-small-size-100">
            <md-field>
              <label>Group</label>
              <md-select v-model="filters.group" @md-selected="onFilterChange">
                <md-option value="">All Groups</md-option>
                <md-option
                    v-for="group in groupOptions"
                    :key="group"
                    :value="group"
                >
                  {{ group }}
                </md-option>
              </md-select>
            </md-field>
          </div>

          <div class="md-layout-item md-size-70 md-small-size-100"></div>
        </div>
      </div>

      <!-- Data Table -->
      <table-data
          ref="table"
          title="Roles"
          :columns="columns"
          :data="table"
          :total="total"
          :loading="isLoading"
          loading-text="Loading roles..."
          :searchable="true"
          search-placeholder="Search by role name..."
          storage-key="roles-list"
          empty-message="No roles found"
          @fetch="onTableFetch"
      >
        <template #toolbar-right>
          <md-button class="md-primary md-round" @click="goToAdd">
            <md-icon>add</md-icon>
            Add Role
          </md-button>
        </template>

        <template #cell-name="{ item }">
          <clickable-link
              :id="item.id"
              resource="Role"
              :text="item.name"
              :stop-propagation="true"
          />
        </template>

        <template #cell-group="{ item }">
          <span v-if="item.group">
            <badge :type="getGroupBadge(item.group)">
              {{ item.group }}
            </badge>
          </span>
          <span v-else class="text-muted">—</span>
        </template>

        <template #cell-user_count="{ item }">
          {{ item.users_count }}
        </template>

        <template #cell-permission_count="{ item }">
          {{ item.permissions_count }}
        </template>

        <template #cell-created_at="{ item }">
          {{ formatDate(item.created_at) }}
        </template>

        <template #actions="{ item }">
          <md-button
              class="md-icon-button md-primary"
              @click="getToView(item.id)"
              title="View"
          >
            <md-icon>visibility</md-icon>
          </md-button>
          <md-button
              class="md-icon-button md-info"
              @click="goToEdit(item.id)"
              title="Edit"
          >
            <md-icon>edit</md-icon>
          </md-button>
          <md-button
              class="md-icon-button md-danger"
              @click="destroy(item.id)"
              title="Delete"
          >
            <md-icon>delete</md-icon>
          </md-button>
        </template>

        <template #empty-actions>
          <md-button class="md-primary md-round" @click="goToAdd">
            <md-icon>add</md-icon>
            Add First Role
          </md-button>
        </template>
      </table-data>
    </template>
  </page-layout>
</template>

<script>
import PageLayout from '@/components/Layouts/PageLayout.vue';
import TableData from '@/components/DataTable/TableData.vue';
import ClickableLink from '@/components/ClickableLink.vue';
import Badge from '@/components/Badge.vue';
import PageMixin from '@/mixins/page-mixin';
import moment from 'moment';
import Swal from 'sweetalert2';

export default {
  name: 'ListRolePage',
  components: {
    TableData,
    PageLayout: PageLayout,
    ClickableLink: ClickableLink,
    Badge: Badge
  },
  mixins: [PageMixin],
  data() {
    return {
      table: [],
      total: 0,
      isLoading: false,
      filters: {
        group: '',
        search: ''
      },
      groupOptions: [
        'Administration',
        'Operations',
        'Finance',
        'Human Resources',
        'System'
      ],
      columns: [
        {field: 'name', label: 'Role Name'},
        {field: 'group', label: 'Group'},
        {field: 'users_count', label: 'Users Count'},
        {field: 'permissions_count', label: 'Permissions Count'},
        {field: 'created_at', label: 'Created At'}
      ],
      tableState: {
        page: 1,
        perPage: 15,
        search: ''
      }
    };
  },
  computed: {
    pageTitle() {
      return 'Roles';
    },
    pageBreadcrumb() {
      return [
        {label: 'Settings', path: '/settings'},
        {label: 'Roles'}
      ];
    },
    pageInfoCards() {
      var total = this.total;
      var groupCount = {};

      this.table.forEach(function (role) {
        var group = role.group || 'Ungrouped';
        groupCount[group] = (groupCount[group] || 0) + 1;
      });

      var groupsUsed = Object.keys(groupCount).length;

      return [
        {
          icon: 'tag',
          label: 'Total Roles',
          value: total,
          color: 'primary'
        },
        {
          icon: 'category',
          label: 'Groups',
          value: groupsUsed,
          color: 'info'
        },
        {
          icon: 'pageview',
          label: 'On This Page',
          value: this.table.length,
          color: 'success'
        },
        {
          icon: 'schedule',
          label: 'Last Updated',
          value: this.getLatestUpdatedText(),
          color: 'warning'
        }
      ];
    }
  },
  created() {
    // DataTable emits `fetch` on mount after restoring its state
  },
  methods: {
    // ============ DATA ============
    async onTableFetch(state) {
      this.tableState = state;
      await this.fetchList();
    },

    async fetchList() {
      this.isLoading = true;
      try {
        var params = {
          filter: {count_user:'users',count_permission:'permission'}
        };

        if (this.tableState.search) {
          params.filter.name = this.tableState.search;
        }

        if (this.filters.group) {
          params.filter.group = this.filters.group;
        }

        params.page = {
          number: this.tableState.page,
          size: this.tableState.perPage
        };

        await this.$store.dispatch('roles/list', params);
        this.table = this.$store.getters['roles/list'] || [];
        this.total = this.$store.getters['roles/listTotal'] || 0;
      } catch (error) {
        console.error('Error fetching roles:', error);
        this.$store.dispatch('alerts/error', 'Failed to load roles');
        this.table = [];
        this.total = 0;
      } finally {
        this.isLoading = false;
      }
    },

    onFilterChange() {
      if (this.$refs.table) {
        this.$refs.table.currentPage = 1;
        this.$refs.table.persistState();
        this.$refs.table.emitFetch();
      }
    },

    refreshList() {
      this.fetchList();
    },

    // ============ ACTIONS ============
    async destroy(id) {
      if (this.$isDemo == 1 && ['1', '2', '3'].indexOf(String(id)) !== -1) {
        await this.$store.dispatch(
            'alerts/error',
            'You are not allowed to change data of default items.'
        );
        return;
      }

      var result = await Swal.fire({
        title: 'Delete this role?',
        text: 'This action cannot be undone.',
        icon: 'warning',
        showCancelButton: true,
        confirmButtonColor: '#f44336',
        cancelButtonColor: '#6c757d',
        confirmButtonText: 'Yes, delete it!',
        cancelButtonText: 'No, keep it'
      });

      if (!result.value) return;

      try {
        await this.$store.dispatch('roles/destroy', id);
        await this.$store.dispatch('alerts/success', 'Role deleted successfully.');
        await this.fetchList();
      } catch (error) {
        if (error.response && error.response.data && error.response.data.errors && error.response.data.errors[0]) {
          this.$store.dispatch('alerts/error', error.response.data.errors[0].title);
        } else {
          this.$store.dispatch('alerts/error', 'Failed to delete role');
        }
      }
    },

    // ============ NAVIGATION ============
    goToAdd() {
      this.$router.push({name: 'Add Role'});
    },

    goToEdit(id) {
      this.$router.push({name: 'Edit Role', params: {id: id}});
    },

    getToView(id) {
      this.$router.push({name: 'View Role', params: {id: id}});
    },

    // ============ HELPERS ============
    getGroupBadge(group) {
      var badges = {
        'staff': 'primary',
        'vendor': 'success'
      };
      return badges[group] || 'default';
    },

    getLatestUpdatedText() {
      if (!this.table.length) return 'N/A';

      var latest = null;
      this.table.forEach(function (role) {
        var d = role.updated_at || role.created_at;
        if (!d) return;
        if (!latest || new Date(d) > new Date(latest)) {
          latest = d;
        }
      });

      if (!latest) return 'N/A';
      return moment(latest).fromNow();
    },

    formatDate(date) {
      if (!date) return 'N/A';
      return moment(date).format('DD MMM YYYY');
    }
  }
};
</script>

<style lang="scss" scoped>
.filters-bar {
  padding: 16px 20px;
  background: #f8f9fa;
  border-radius: 10px;
  margin-bottom: 20px;
}

.text-muted {
  color: #999;
}
</style>